<template>
  <BaseLayout :title="`Edit Task: ${subtask.homework_title}: ${subtask.title}`">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div class="card">
          <h5 class="card-header">Edit Task</h5>
          <div class="card-body">
            <SubtaskForm :subtask="subtask" buttonText="Save Changes" @submitForm="updateSubtask" />
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div class="card p-3">
        <h5 class="card-title">Quick Actions:</h5>
        <router-link :to="`/homework/${subtask.homework_id}`" class="btn btn-primary mb-2">
          <i class="bi bi-arrow-left"></i> Back to Homework
        </router-link>
        <button @click="downloadFiles([...subtask.resource_file_paths, ...subtask.submission_file_paths])" class="btn btn-success mb-2">
          <i class="bi bi-download"></i> Download subtask files
        </button>
        <button @click="downloadFiles(subtask.test_file_paths)" class="btn btn-warning mb-2">
          <i class="bi bi-download"></i> Download test files
        </button>
        <button @click="deleteSubtask(subtask.id)" class="btn btn-danger mb-2">
          <i class="bi bi-trash"></i> Delete Task
        </button>
      </div>
      <div class="card p-3 mt-3">
        <h5 class="card-title">Help:</h5>
        <button class="btn btn-primary mb-2" @click="openHelpInNewTab('evaluation')">
          <i class="bi bi-question-circle"></i> Open Help in New Tab for Evaluation
        </button>
        <button class="btn btn-info mb-2" @click="downloadTestSubtask">
          <i class="bi bi-eye"></i> Download Example Test Script (SQL)
        </button>
        <button class="btn btn-info mb-2" @click="downloadTestSubtaskPython">
          <i class="bi bi-eye"></i> Download Example Test Script (Python)
        </button>
        <button class="btn btn-primary mb-2" @click="openHelpInNewTab('pre_check')">
          <i class="bi bi-question-circle"></i> Open Help in New Tab for Pre-check
        </button>
        <button class="btn btn-info" @click="downloadPreCheckSubmission">
          <i class="bi bi-eye"></i> Download Example Pre-check Script
        </button>
      </div>
    </template>

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import SubtaskForm from '@/components/SubtaskForm.vue';
import SubtaskController from '@/controllers/SubtaskController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import { mapGetters } from 'vuex';
// Set base dynamically based if it is running on university server or not
const base = process.env.VUE_APP_UNI_SERVER === 'True' ? '/pa036-hw/' : '/';

export default {
  name: 'SubtaskEditView',
  components: {
    BaseLayout,
    SubtaskForm,
    StemErrorModal,
  },
  data() {
    return {
      subtask: {},
      homework: {},
      loading: true,
    };
  },
  computed: {
    ...mapGetters(['isTeacher'])
  },
  async created() {
    try {
      // Check if the user is a teacher
      if (!this.isTeacher) {
        this.$store.dispatch('triggerAlert', {
          type: 'error',
          title: 'Error',
          message: 'You are not authorized to access this page.',
        });
        this.$router.push(`/homeworks`);
        return;
      }
      const subtaskId = this.$route.params.id;
      this.subtask = await SubtaskController.fetchSubtaskById(subtaskId);
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async updateSubtask(updatedSubtask) {
      try {
        // Update subtask metadata
        await SubtaskController.updateSubtask(updatedSubtask.id, updatedSubtask);

        // Upload new files
        await this.uploadFiles(updatedSubtask.id, updatedSubtask.homework_id, updatedSubtask);

        // Delete files
        await this.deleteFiles(updatedSubtask.id, updatedSubtask.filesToDelete);

        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Task updated',
          message: 'The task was successfully updated',
        });
        this.$router.push(`/subtask/${updatedSubtask.id}`);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async deleteSubtask(subtaskId) {
      if (confirm('Are you sure you want to delete this task?')) {
        try {
          await SubtaskController.deleteSubtask(subtaskId);

          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'Task deleted successfully',
          });
          this.$router.push(`/homework/${this.subtask.homework_id}`);
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', error.message);
        }
      }
    },
    async uploadFiles(subtaskId, homeworkId, updatedSubtask) {
      if (updatedSubtask.resource_files.length > 0) {
        const resourceFormData = new FormData();
        for (const file of updatedSubtask.resource_files) {
          resourceFormData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_resources', subtaskId, homeworkId, resourceFormData);
      }

      if (updatedSubtask.submission_files.length > 0) {
        const submissionFormData = new FormData();
        for (const file of updatedSubtask.submission_files) {
          submissionFormData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_submissions', subtaskId, homeworkId, submissionFormData);
      }

      if (updatedSubtask.test_files.length > 0) {
        const testFormData = new FormData();
        for (const file of updatedSubtask.test_files) {
          testFormData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_tests', subtaskId, homeworkId, testFormData);
      }
      
      if (updatedSubtask.precheck_files.length > 0) {
        const testPreCheckFormData = new FormData();
        for (const file of updatedSubtask.precheck_files) {
          testPreCheckFormData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_pre_check_tests', subtaskId, homeworkId, testPreCheckFormData);
      }
    },
    // Download multiple files
    async downloadFiles(filePaths) {
      try {
        const response = await SubtaskController.downloadFiles(filePaths);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'subtask_files.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async deleteFiles(subtaskId, filesToDelete) {
      console.log('Deleting files:', filesToDelete);
      
      for (const filePath of filesToDelete.resource_file_paths) {
        await SubtaskController.deleteFile(subtaskId, filePath);
      }
      for (const filePath of filesToDelete.submission_file_paths) {
        await SubtaskController.deleteFile(subtaskId, filePath);
      }
      for (const filePath of filesToDelete.test_file_paths) {
        await SubtaskController.deleteFile(subtaskId, filePath);
      }
      for (const filePath of filesToDelete.test_pre_check_file_paths) {
        await SubtaskController.deleteFile(subtaskId, filePath);
      }
    },
    putListTogeter(list1, list2) {
      return list1.concat(list2);
    },
    openHelpInNewTab(targetUrl) {
      var urls = {
        'evaluation': '#worker_evaluation',
        'pre_check': '#pre_check',
      };
      
      // Open the help page in a new tab and scroll to the specific section
      window.open(base + 'help' + (urls[targetUrl] || ''), '_blank');
    },
    downloadTestSubtask() {
      window.open(base + 'assets/test_subtask.sh', '_blank');
    },
    downloadTestSubtaskPython() {
      window.open(base + 'assets/test_subtask.python-example.sh', '_blank');
    },
    downloadPreCheckSubmission() {
      window.open(base + 'assets/pre_check_submission.sh', '_blank');
    },
  },
};
</script>